import { ActionIcon, Tabs } from '@mantine/core';
import { useModals } from '@mantine/modals';
import { IconPlus } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Outlet, useSearchParams } from 'react-router-dom';

import { AccessDenied } from 'src/Components/AccessDenied';
import { BankAccountList } from 'src/Components/BankAccounts/BankAccountList';
import { BudgetFilters } from 'src/Components/Expenses/BudgetFilters';
import BudgetList from 'src/Components/Expenses/BudgetList/BudgetList.component';
import { BudgetEntry } from 'src/Components/Expenses/BudgetList/BudgetList.types';
import { BudgetYearPanel } from 'src/Components/Expenses/BudgetYearPanel';
import { DeductibleCharges } from 'src/Components/Expenses/DeductibleCharges';
import { FinancesHome, TabKey } from 'src/Components/Expenses/FinancesHome';
import { OperationsToolbar } from 'src/Components/Expenses/OperationsToolbar';
import { PropertyBalanceTable } from 'src/Components/Expenses/PropertyBalanceTable';
import { SendMyStatementButton } from 'src/Components/Expenses/SendMyStatementButton';
import { SubPageHeader } from 'src/Components/Expenses/SubPageHeader';
import { FundCallList } from 'src/Components/FundCalls/FundCallList';
import { PageHeader } from 'src/Components/Navigation/PageHeader';
import { YearSelector } from 'src/Components/Shared/YearSelector';
import { useFetchAssetDetails } from 'src/Hooks/Queries/useFetchAssetDetails';
import useFetchBudgetForecastView from 'src/Hooks/Queries/useFetchBudgetForecastView';
import useFetchExpenses from 'src/Hooks/Queries/useFetchExpenses';
import useFetchExpenseStats from 'src/Hooks/Queries/useFetchExpenseStats';
import useFetchMoneyTransfers from 'src/Hooks/Queries/useFetchMoneyTransfers';
import useFetchUser from 'src/Hooks/Queries/useFetchUser';
import useBudgetFilters from 'src/Hooks/Shared/useBudgetFilters';
import { useCanCreate, useCanRead, useHasPermission } from 'src/Hooks/Shared/useHasPermission';
import { useIsAssetAboveQuotas } from 'src/Hooks/Shared/useIsAssetAboveQuotas';
import useIsMobile from 'src/Hooks/Shared/useIsMobile';
import useNavigateKeepingSearch from 'src/Hooks/Shared/useNavigateKeepingSearch';
import { AssetDetailsDtoRentalDeclarationRegimeEnum } from 'src/Services/API/generated';
import { trackFinancesActionClicked } from 'src/Services/Mixpanel/events';
import { enrichBudgetEntry } from 'src/Utils/budgetEntry';

import ExpenseBalanceList from '../../../Components/Expenses/ExpenseBalanceList/ExpenseBalanceList.component';
import { NetBalanceHeader } from '../../../Components/Expenses/NetBalanceHeader';
import VideoTutorialButton from '../../../Components/Shared/VideoTutorialButton/VideoTutorialButton.component';

import styles from './ExpensesMainPage.module.scss';

const YEAR_TABS: TabKey[] = ['operations', 'forecast', 'deductibleCharges'];

const KNOWN_TABS: TabKey[] = [
  'forecast',
  'operations',
  'bankAccounts',
  'userBalance',
  'deductibleCharges',
  'fundCalls',
  // 'assetBalance' and 'simulator' — values kept in TabKey but no longer rendered as a panel.
  // Leaving them out here is what makes a stored or linked 'simulator' fall back to the hub
  // instead of opening a blank tab.
];

const ExpensesMainPage = () => {
  const { t } = useTranslation();
  const modals = useModals();
  const navigateKeepingSearch = useNavigateKeepingSearch();
  const isMobile = useIsMobile();

  // Permission checks
  const { hasPermission: canRead, ownerName } = useCanRead('expense');
  const { hasPermission: canCreate } = useCanCreate('expense');
  const { hasPermission: canReadDeductibleCharges } = useHasPermission('deductibleCharges', 'read');

  const { data: asset } = useFetchAssetDetails();
  const assetId = asset?.id;

  const showDeductibleChargesTab =
    canReadDeductibleCharges &&
    asset?.rentalDeclarationRegime === AssetDetailsDtoRentalDeclarationRegimeEnum.Reel &&
    !asset?.hasDismemberment;

  const [searchParams, setSearchParams] = useSearchParams();

  const viewParam = searchParams.get('view');
  const value: TabKey | null = (KNOWN_TABS as string[]).includes(viewParam ?? '')
    ? (viewParam as TabKey)
    : null;

  const yearParam = searchParams.get('year');
  const selectedYear = yearParam ? parseInt(yearParam, 10) : new Date().getFullYear();

  const setValue = (next: TabKey | null) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        if (next === null) {
          params.delete('view');
        } else {
          params.set('view', next);
        }
        return params;
      },
      { replace: false }
    );
  };

  const handleYearChange = (year: number) => {
    setSearchParams(
      prev => {
        const params = new URLSearchParams(prev);
        params.set('year', String(year));
        return params;
      },
      { replace: true }
    );
  };

  const {
    data: transactions,
    isLoading: expenseLoading,
    isFetchingMore: expenseFetchingMore,
  } = useFetchExpenses(selectedYear);
  const { data: transfers, isLoading: transfersLoading } = useFetchMoneyTransfers(selectedYear);
  const { data: statsData } = useFetchExpenseStats(selectedYear.toString());
  const { data: forecastView } = useFetchBudgetForecastView(selectedYear);

  const availableYears = useMemo(() => {
    const currentYear = new Date().getFullYear();
    const years = statsData?.availableYears ?? [currentYear];
    if (!years.includes(currentYear)) return [currentYear, ...years].sort((a, b) => a - b);
    return years;
  }, [statsData?.availableYears]);

  const allOperations = useMemo(() => {
    const expenseEntries = (transactions || []).map(e => ({
      ...e,
      kind: e.isIncome ? 'income' : 'expense',
      dateObj: e.date.toDate(),
    })) as BudgetEntry[];

    const transferEntries = (transfers || []).map(tr => ({
      ...tr,
      kind: 'transfer',
      dateObj: new Date(tr.date),
    })) as BudgetEntry[];

    const virtualEntries: BudgetEntry[] = [];
    for (const line of forecastView?.lines ?? []) {
      if (line.source !== 'AUTO') continue;
      for (const occurrence of line.occurrences) {
        if (occurrence.isMaterialized) continue;
        const dateObj = new Date(occurrence.date);
        virtualEntries.push({
          id: `virtual-${line.id}-${occurrence.date}`,
          date: dayjs(dateObj),
          title: line.title,
          amount: occurrence.amount,
          currency: 'EUR',
          isIncome: line.isIncome,
          type: line.expenseType ?? 'OTHER_TAX',
          shares: [],
          kind: line.isIncome ? 'income' : 'expense',
          paidBy: line.paidBy ?? undefined,
          paidByBankAccount: line.paidByBankAccount ?? undefined,
          dateObj,
          isVirtual: true,
          templateExpenseId: occurrence.templateExpenseId ?? undefined,
          occurrenceDate: occurrence.date,
        } as unknown as BudgetEntry);
      }
    }

    return [...expenseEntries, ...transferEntries, ...virtualEntries].sort(
      (a, b) => b.dateObj.getTime() - a.dateObj.getTime()
    );
  }, [transactions, transfers, forecastView]);

  const { data: user } = useFetchUser();
  const assetAboveQuotas = useIsAssetAboveQuotas();

  const enrichedOperations = useMemo(
    () =>
      allOperations.map(entry =>
        enrichBudgetEntry(entry, user?.id, {
          transferTitle: t('expenses.transferTitle', 'Remboursement'),
          categoryLabel: (type: string) => t(`expense.type.${type}`),
          invitedUserLabel: t('checklist.invitedUser', 'Invité'),
        })
      ),
    [allOperations, user?.id, t]
  );

  const {
    filters,
    setFilters,
    resetFilters,
    filteredEntries,
    activeFilterCount,
    availableCategories,
    availablePayers,
    summary,
  } = useBudgetFilters(enrichedOperations, selectedYear);

  // Show access denied if user doesn't have read permission
  if (!canRead) {
    return <AccessDenied ownerName={ownerName} />;
  }

  const openOperationTypeModal = () => {
    if (!assetId) return;
    modals.openContextModal('operationType', {
      title: t('operationType.modalTitle', 'Que souhaitez-vous faire ?'),
      innerProps: { assetId },
      size: 'md',
    });
  };

  const handleAddExpenseFromHome = () => {
    trackFinancesActionClicked({ action: 'add_expense', source: 'home' });
    navigateKeepingSearch('new', { state: { isIncome: false } });
  };

  const showYearSelector = value !== null && YEAR_TABS.includes(value);

  return (
    <div className={styles.container}>
      <PageHeader
        label={t('expenses.finances', 'Finances')}
        onLabelClick={value !== null ? () => setValue(null) : undefined}
        rightElement={
          isMobile ? (
            <div className={styles.headerRight}>
              {canCreate && (
                <ActionIcon
                  radius={'xl'}
                  size={'lg'}
                  onClick={openOperationTypeModal}
                  disabled={assetAboveQuotas}
                >
                  <IconPlus size={20} />
                </ActionIcon>
              )}
            </div>
          ) : (
            <VideoTutorialButton videoUrl={'https://youtu.be/0YeV1WDnf2c'} />
          )
        }
      />

      {value === null ? (
        <FinancesHome
          year={selectedYear}
          operationsCount={enrichedOperations.length}
          showTaxTile={!!showDeductibleChargesTab}
          canCreate={canCreate}
          assetAboveQuotas={assetAboveQuotas}
          onNavigate={setValue}
          onAddExpense={handleAddExpenseFromHome}
        />
      ) : (
        <>
          <SubPageHeader
            title={t(`expenses.tab.${value}`)}
            onBack={() => setValue(null)}
            rightElement={
              showYearSelector ? (
                <>
                  <span>{t('expenses.year.label', 'Année')}</span>
                  <YearSelector
                    year={selectedYear}
                    availableYears={availableYears}
                    onChange={handleYearChange}
                  />
                </>
              ) : undefined
            }
          />
          <Tabs value={value} onChange={next => setValue(next as TabKey | null)}>
            <Tabs.Panel value="operations">
              <OperationsToolbar />
              <BudgetFilters
                filters={filters}
                onFiltersChange={setFilters}
                onReset={resetFilters}
                activeFilterCount={activeFilterCount}
                availableCategories={availableCategories}
                availablePayers={availablePayers}
                summary={summary}
              />
              <BudgetList
                entries={filteredEntries}
                loading={expenseLoading || transfersLoading}
                fetchingMore={expenseFetchingMore}
                currentUserId={user?.id}
                onResetFilters={resetFilters}
                hasActiveFilters={activeFilterCount > 0}
              />
            </Tabs.Panel>
            <Tabs.Panel value="userBalance">
              <NetBalanceHeader />
              <SendMyStatementButton />
              <ExpenseBalanceList />
              {/* Ma position, puis celle de tout le monde, sur le même écran : c'est là que
                  se lit d'un coup d'œil que les deux calculs de solde disent la même chose. */}
              <PropertyBalanceTable />
            </Tabs.Panel>
            <Tabs.Panel value="bankAccounts">
              <BankAccountList />
            </Tabs.Panel>
            <Tabs.Panel value="forecast">
              {/* Prévisionnel v2: « Préparer » (wizard) and « Suivre » (planned vs real).
                  The former per-category editor and its recurrence-fed detailed view are no
                  longer mounted — their code is kept, the screen is not. */}
              <BudgetYearPanel year={selectedYear} />
            </Tabs.Panel>
            <Tabs.Panel value="fundCalls">
              <FundCallList canIssueForAll={canCreate} />
            </Tabs.Panel>
            {showDeductibleChargesTab && (
              <Tabs.Panel value="deductibleCharges">
                <DeductibleCharges year={selectedYear} />
              </Tabs.Panel>
            )}
          </Tabs>
        </>
      )}

      <Outlet />
    </div>
  );
};

export default ExpensesMainPage;
